import {Injectable} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {capitalize} from '../../../utils/string-utils';
import {MxNotificationService} from '../../../shared/services/mx-notification.service';
import {MxCollectionDataService} from '../../mx-grid/services/mx-collection-data.service';
import {MxGridButtonsConfig} from '../../mx-grid/services/mx-grid-button.model';
import {MxGridService} from '../../mx-grid/services/mx-grid-customizer-event';
import {DefaultGridButtons} from '../../mx-grid/services/mx-grid-enums';
import {MxSelection} from '../../mx-grid/services/selection-type.model';
import {AssignmentResult} from './mx-assignment-grid';
import {MxAssignmentGridActionsConfig} from './mx-assignment-grid-actions-config';
import {MxAssignmentGridDialogService} from './mx-assignment-grid-dialog.service';

@Injectable({
    providedIn: 'root'
})
export class MxAssignmentGridActionsService {

    constructor(private notificationService: MxNotificationService,
                private dataService: MxCollectionDataService,
                private assignmentGridDialogService: MxAssignmentGridDialogService,
                private router: Router,
                private route: ActivatedRoute
    ) {}

    public customizeActions(config: MxAssignmentGridActionsConfig): MxGridButtonsConfig {
        config = Object.assign({...new MxAssignmentGridActionsConfig()}, {...config});

        let boGridService: () => MxGridService;
        let updateAssignedItems: () => void = () => {};
        let assignedItems: Array<any>;
        let itemsLoading: boolean = false;
        let updateLoadingProcess: (isLoading: boolean, service: () => MxGridService) => void =
            (isLoading: boolean, service: () => MxGridService) => {
                itemsLoading = isLoading;
                if (service && service().actions) {
                    service().actions.refreshButtons();
                }
            };
        if (config.assignedItemsConfig && config.assignedItemsConfig.source) {
            updateAssignedItems = () => {
                updateLoadingProcess(true, boGridService);
                config.assignedItemsConfig.source
                    .subscribe(value => {
                        assignedItems = value;
                        updateLoadingProcess(false, boGridService);
                    });
            };
            updateAssignedItems();
        }
        let doSave = (restLink: string, rows: Array<any>, action: 'save' | 'remove'): void => {
            updateLoadingProcess(true, boGridService);
            // TODO should be notify by key if possible
            this.notificationService.notify(this.dataService.update(restLink, rows),
                action === 'save' && config.saveSuccessMessage
                    ? config.saveSuccessMessage
                    : `${capitalize(config.recordsName)} were ${action}d`,
                `Error with ${action} ${config.recordsName}`)
                .subscribe(() => {
                    if (config.reloadAfterSave) {
                        boGridService().collection.doReload();
                    }
                    if (config.callAfterSave) {
                        config.callAfterSave();
                    }
                    if (config.callAfterRemove && action === 'remove') {
                        config.callAfterRemove();
                    }
                    updateLoadingProcess(false, boGridService);
                    updateAssignedItems();
                } , config.handleSaveError ? () => {} : undefined);
        };
        config.event.customizeRemoveConfig({
            recordsName: config.recordsName
        });
        config.event.onSetUp(service => boGridService = () => service);
        let callBackAction = config.callBackAction
            ? (result: AssignmentResult) => config.callBackAction(result, updateAssignedItems)
            : (result: AssignmentResult): void => {
                if (config.saveAssignments) {
                    doSave(config.restUrl ? config.restUrl :
                        this.router.url, result.assignedItemsKeys, 'save');
                }
                if (!config.reloadAfterSave) {
                    boGridService().collection.setRows(result.assignedItems);
                }
        };
        let buttonConfig: MxGridButtonsConfig = new MxGridButtonsConfig([
            [ DefaultGridButtons.add, {
                hidden: false,
                clickHandler: () => {
                    this.assignmentGridDialogService.openAssignmentDialog(
                            {
                                componentName: config.componentName,
                                reportName: config.reportName,
                                assignedItems:
                                    config.assignedItemsConfig && config.assignedItemsConfig.map
                                        ? config.assignedItemsConfig
                                            .map(boGridService().collection.rows, assignedItems)
                                        : boGridService().collection.rows,
                                disableRowFunc: config.gridDisableRowFn,
                                selectionChangedFunc: config.gridSelectionChangedFn,
                                filterDefaults:
                                    typeof config.filterDefaults === 'function' ? config.filterDefaults() : config.filterDefaults,
                                selectionMode: config.selectionMode,
                                props: config.props,
                                callbackAction: callBackAction,
                                assignmentConfig: config.assignmentConfig
                            });
                },
                disabled: () =>  itemsLoading ? 'Waiting data from server' : false
            }],
            [ DefaultGridButtons.edit, {hidden: true, disabled: true}],
            [ DefaultGridButtons.view, {hidden: true, disabled: true}]
        ]);
        if (config.overrideRemoveAction) {
            buttonConfig.set(DefaultGridButtons.remove, {
                hidden: false,
                disabled: selection => MxSelection.noneSelection(selection,
                    row => itemsLoading,
                    'Waiting data from server'
                ),
                clickHandler: () => {
                    boGridService().collection.confirmRemove()
                        .subscribe( (ok: boolean) => {
                            if (ok) {
                                if (config.saveAssignments) {
                                    const selected = boGridService().collection.selection.selected.slice();
                                    boGridService().collection.removeSelectedItemsFromGrid();
                                    const removedIds = config.assignedItemsConfig
                                    && config.assignedItemsConfig.removedMap
                                        ? config.assignedItemsConfig.removedMap(selected, assignedItems)
                                        : [...boGridService().collection.rows]
                                                .map(item => boGridService().collection.extractId(item));
                                    doSave(config.restUrl
                                        ? config.restUrl
                                        : this.router.url, removedIds, 'remove');
                                }
                            }
                        });
                }
            });
        }
        if (config.permissionFromRoute) {
            buttonConfig.get(DefaultGridButtons.add).permission = this.route.snapshot.data.permission;
            buttonConfig.get(DefaultGridButtons.remove).permission = this.route.snapshot.data.permission;
        }
        return buttonConfig;
    }
}
